import React, { useEffect, useRef, useState } from 'react';

import { processTextWithLinks } from '../../utilities/processTextWithLinks';
import { setupShadowDOMOverride } from '../../utilities/setupShadowDOMOverride';
import styles from './google-maps-location-picker.module.css';

/**
 * Loads the Google Maps Places API (New) script dynamically.
 * @returns {Promise<void>} Resolves when the script is loaded.
 */

// Get API key from environment variable (replaced at build time by Vite).
// Note: This must be in a .env file in the project root and the dev server must be restarted.
const apiKey = import.meta.env.VITE_STARMAP_API_GOOGLE_PLACES_KEY;

// @ts-ignore - this is a global function that is used to initialize the Places API.
window.initPlaces = () => {
  document.dispatchEvent(new CustomEvent('places-api-initialized'));
};

const loadGoogleMapsScript = () => {
  return new Promise((/** @type {(value?: void) => void} */ resolve, reject) => {
    // Check if Google Maps API is already loaded with Places library.
    // @ts-expect-error - Google Maps API types not available
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    // Check if script is already being loaded.
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    if (!apiKey) {
      reject(
        new Error(
          'Google Maps API key not found. Please create a .env file in the project root with VITE_STARMAP_API_GOOGLE_PLACES_KEY=your_key_here and restart the dev server.'
        )
      );
      return;
    }

    // Load the Google Maps Places API (New) script with places library.
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=initPlaces`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
};

/**
 * GoogleMapsLocationPicker component displays a google maps location picker input with autocomplete functionality using Places API (New).
 *
 * @param {Object} props - The component props
 * @param {string} props.id - The input id
 * @param {string} props.name - The input name
 * @param {boolean} props.required - Whether the input is required
 * @param {string} props.labelText - The label text
 * @param {string} props.locationPickerHeading - The location picker heading
 * @param {function} props.onChange - Callback when location selection changes
 * @param {Object} props.autocompleteOptions - Options to pass to PlaceAutocompleteElement (requestOptions, componentRestrictions, etc.)
 * @param {string} props.defaultValue - Default value to display in the input
 * @returns {import('react').JSX.Element} The rendered component
 */
export const GoogleMapsLocationPicker = ({
  id,
  name,
  required = false,
  labelText,
  locationPickerHeading,
  onChange,
  autocompleteOptions = {},
  defaultValue = ''
}) => {
  const containerRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const autocompleteElementRef = useRef(/** @type {any} */ (null));
  const onChangeRef = useRef(onChange);
  const hasInitializedRef = useRef(false);
  const [isAutocompleteActive, setIsAutocompleteActive] = useState(false);

  // Keep onChange ref in sync without causing re-renders.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    // Initialize Google Maps Places API (New) PlaceAutocompleteElement when component mounts.
    const initAutocomplete = async () => {
      try {
        if (!containerRef.current || !inputRef.current) {
          return;
        }

        // @ts-expect-error - Google Maps API types not available
        const PlaceAutocompleteElement = window.google?.maps?.places?.PlaceAutocompleteElement;

        if (!PlaceAutocompleteElement) {
          throw new Error(
            'Google Maps Places API (New) not available. ' +
              'Ensure "Places API (New)" (not just "Places API") is enabled in your Google Cloud project. ' +
              'Also verify that Maps JavaScript API is enabled.'
          );
        }
        // Create PlaceAutocompleteElement.
        autocompleteElement = new PlaceAutocompleteElement();
        // Configure request options.
        const requestConfig = {
          types: autocompleteOptions.types || autocompleteOptions.requestOptions?.types || ['geocode'],
          ...(autocompleteOptions.componentRestrictions && {
            componentRestrictions: autocompleteOptions.componentRestrictions
          }),
          ...(autocompleteOptions.requestOptions && autocompleteOptions.requestOptions)
        };

        autocompleteElement.request = requestConfig;
        autocompleteElement.setAttribute('id', `${id}-autocomplete`);
        autocompleteElement.style.width = '100%';
        autocompleteElement.style.display = 'block';
        autocompleteElement.className = 'field__input ' + styles['google-maps-location-picker__input'];
        // Remove any existing PlaceAutocompleteElement before inserting.
        if (currentContainer) {
          const existingElements = currentContainer.querySelectorAll('gmp-place-autocomplete');
          existingElements.forEach((el) => el.remove());
        }
        // Insert PlaceAutocompleteElement into DOM.
        if (!autocompleteElement.parentNode && inputRef.current?.parentNode) {
          const parent = inputRef.current.parentNode;
          if (inputRef.current.nextSibling) {
            parent.insertBefore(autocompleteElement, inputRef.current.nextSibling);
          } else {
            parent.appendChild(autocompleteElement);
          }
        }
        autocompleteElementRef.current = autocompleteElement;
        hasInitializedRef.current = true;
        // Mark autocomplete as active so we can hide the original input.
        setIsAutocompleteActive(true);
        // Wait for shadow DOM to initialize.
        await new Promise((resolve) => setTimeout(resolve, 150));
        // Configure internal input styling and attributes.
        const internalInput = getInternalInput(autocompleteElement);
        if (internalInput && inputRef.current) {
          internalInput.className = inputRef.current.className;
          internalInput.id = inputRef.current.id || `${id}-internal`;
          internalInput.name = inputRef.current.name;
          internalInput.required = inputRef.current.required;

          if (defaultValue && !internalInput.value) {
            internalInput.value = defaultValue;
          }
          // Keep hidden input in sync with internal input changes.
          internalInput.addEventListener('input', () => {
            if (inputRef.current && internalInput.value) {
              inputRef.current.value = internalInput.value;
            }
          });
        }

        // Listen for place selection.
        autocompleteElement.addEventListener('gmp-select', async (event) => {
          try {
            const place = event.placePrediction.toPlace();
            // Fetch additional fields.
            await place
              .fetchFields({
                fields: ['displayName', 'formattedAddress', 'location', 'addressComponents', 'id']
              })
              .catch((error) => {
                console.warn('Error fetching place fields:', error);
              });

            const address = place.formattedAddress || '';
            const locationPlaceData = extractPlaceData(place);
            // Get the internal input to update its value.
            const currentInternalInput = getInternalInput(autocompleteElement);
            // Store in hidden input.
            if (inputRef.current) {
              inputRef.current.value = address;
              inputRef.current.setAttribute('data-location-place', JSON.stringify(locationPlaceData));
            }
            // Update the visible internal input value to keep it displayed.
            // Use setTimeout to ensure PlaceAutocompleteElement has finished processing.
            if (currentInternalInput) {
              // Set immediately.
              currentInternalInput.value = address;
              // Also set after a short delay to ensure it persists.
              setTimeout(() => {
                if (currentInternalInput && currentInternalInput.value !== address) {
                  currentInternalInput.value = address;
                }
              }, 100);
            }
            // Call onChange callback using ref to avoid triggering re-initialization.
            if (onChangeRef.current) {
              onChangeRef.current(address, locationPlaceData);
            }
          } catch (error) {
            console.error('Error handling place selection:', error);
          }
        });
      } catch (error) {
        console.error('Failed to load Google Maps Places API (New):', error);
      }
    };

    document.addEventListener('places-api-initialized', initAutocomplete);
    loadGoogleMapsScript();
    // Set up shadow DOM override before creating the element.
    const cleanupShadowOverride = setupShadowDOMOverride();
    // Capture container reference for cleanup.
    const currentContainer = containerRef.current;
    let autocompleteElement = null;
    // Check if already initialized to prevent re-initialization when element gets redrawn.
    if (hasInitializedRef.current || autocompleteElementRef.current) {
      // Clean up shadow override if we're not initializing.
      cleanupShadowOverride();
      return;
    }
    // Also check if one already exists in the DOM.
    if (currentContainer) {
      const existingElement = currentContainer.querySelector('gmp-place-autocomplete');
      if (existingElement) {
        return;
      }
    }
    // Helper function to get internal input from PlaceAutocompleteElement.
    const getInternalInput = (element) => {
      if (element.shadowRoot) {
        return element.shadowRoot.querySelector('input');
      }
      return element.querySelector('input');
    };
    // Helper function to extract serializable place data.
    const extractPlaceData = (place) => {
      try {
        return {
          displayName: place.displayName || null,
          formattedAddress: place.formattedAddress || null,
          location: place.location
            ? {
                lat: typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat,
                lng: typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng
              }
            : null,
          addressComponents: place.addressComponents
            ? place.addressComponents
                .map((comp) => ({
                  longText: comp.longText || null,
                  shortText: comp.shortText || null,
                  types: comp.types || []
                }))
                .filter((comp) => comp !== null)
            : null,
          id: place.id || null
        };
      } catch (error) {
        console.error('Error extracting place data:', error);
        return {
          formattedAddress: place.formattedAddress || null
        };
      }
    };

    // Cleanup function - only run on unmount, not when props change.
    return () => {
      // Restore original attachShadow method.
      cleanupShadowOverride();
      // Only cleanup if component is actually unmounting.
      // Don't cleanup on prop changes to prevent element from being redrawn.
      const currentElement = autocompleteElementRef.current;
      if (currentElement && hasInitializedRef.current) {
        // Remove from DOM if it has a parent.
        if (currentElement.parentNode) {
          currentElement.parentNode.removeChild(currentElement);
        }
        autocompleteElementRef.current = null;
        hasInitializedRef.current = false;
        setIsAutocompleteActive(false);
      }
      // Also remove any PlaceAutocompleteElement from the container (safety check).
      if (currentContainer) {
        const remainingElements = currentContainer.querySelectorAll('gmp-place-autocomplete');
        remainingElements.forEach((el) => el.remove());
      }

      document.removeEventListener('places-api-initialized', initAutocomplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // Only re-initialize if apiKey or id changes, not on defaultValue or autocompleteOptions

  return (
    <div ref={containerRef} className={styles['google-maps-location-picker'] + ' field'}>
      <label className={'field__label'} htmlFor={id}>
        {processTextWithLinks(labelText)}
      </label>
      {/* Hidden input for form submission - will be replaced by PlaceAutocompleteElement */}
      <input
        ref={inputRef}
        className={`${styles['google-maps-location-picker__input']} ${isAutocompleteActive ? styles['google-maps-location-picker__input--hidden'] : ''} field__input`}
        id={id}
        name={name}
        type="text"
        required={required}
        autoComplete="off"
      />
      <div className={styles['google-maps-location-picker__label']}>
        <p className={'body-xs'}>{processTextWithLinks(locationPickerHeading)}</p>
      </div>
    </div>
  );
};
